<template>
    <div id="mobile_event_popover" v-show="isOpen" v-if="appointment">
        <!-- Non-block-time appointments -->
        <div v-if="appointment.type !== 'BLOCK_TIME'">
            <div class="d-flex justify-content-between align-items-center">
                <div class="event-popover-details">
                    <span class="event-time">{{
                        appointment.from + " - " + appointment.to
                    }}</span>

                    <template v-if="currentUser.isInLawIndustry">
                        <span class="event-client">{{
                            appointment.subject.subjectNumber
                        }}</span>
                        <br />
                        <span class="event-service">
                            {{ appointment.subject.courtName }} -
                            {{ appointment.subject.courtNumber }}
                        </span>
                        <br />
                    </template>

                    <span class="event-client">{{ appointmentTitle }}</span>
                    <span
                        class="event-service"
                        :class="{
                            'event-service-deleted':
                                appointment.service.deleted,
                        }"
                    >
                        {{ appointment.service.name }}
                    </span>

                    <span class="event-client">{{
                        $t("calendar.appointment.employee")
                    }}</span>
                    <span class="event-service">
                        {{ appointment.employee.first_name }}
                        {{ appointment.employee.last_name }}
                    </span>
                </div>
            </div>

            <div v-if="canManageAppointment()" class="event-notes">
                <span class="event-notes-label">
                    {{ $t("calendar.appointment.notesLabel") }}:
                </span>
                <span class="event-notes-text">{{ appointmentNotes }}</span>
            </div>

            <div v-if="canManageAppointment()" class="event-action-buttons">
                <button class="event-cancel-btn" @click="cancelAppointment">
                    {{ $t("calendar.appointment.cancelAppointment") }}
                </button>
                <button class="event-edit-btn" @click="editAppointment">
                    {{ $t("calendar.appointment.editAppointment") }}
                </button>
                <button
                    v-if="canApproveAppointment(appointment)"
                    class="event-approve-btn"
                    @click="approveAppointment"
                >
                    {{ $t("calendar.appointment.approveAppointment") }}
                </button>
            </div>
        </div>

        <!-- Block time appointments -->
        <div v-else>
            <div class="d-flex justify-content-between align-items-center">
                <div class="event-popover-details">
                    <span class="event-time">{{
                        appointment.from + " - " + appointment.to
                    }}</span>
                    <span class="event-client">{{ appointment.notes }}</span>
                </div>
            </div>

            <div class="event-action-buttons">
                <button class="event-cancel-btn" @click="removeBlockTime">
                    {{ $t("calendar.blockTime.removeBlockTime") }}
                </button>
                <button class="event-edit-btn" @click="editBlockTime">
                    {{ $t("calendar.blockTime.editBlockTime") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { mapGetters } from "vuex";

export default {
    name: "MobileEventPopover",

    props: {
        /**
         * Appointment (event) object
         * @type {Object}
         */
        appointment: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            isOpen: false,
        };
    },

    computed: {
        ...mapGetters(["currentUser"]),

        /**
         * Return appointment title
         * @return {String}
         */
        appointmentTitle() {
            if (!this.appointment) return "Unknown";

            if (this.appointment.type === "REGULAR") {
                return this.appointment.client?.fullName ?? "Unknown";
            }

            if (this.appointment.type === "GROUP") {
                return this.appointment.clients
                    ? `${this.appointment.clients.length}/${
                          this.appointment.service?.sameTimePeople ?? "*"
                      } ${this.$t("calendar.calendarEventCard.clientsLabel")}`
                    : "Unknown";
            }

            return "Unknown";
        },

        /**
         * Return appointment notes text
         * @return {String}
         */
        appointmentNotes() {
            if (!this.appointment) return "";

            return (
                this.appointment.notes ||
                this.$t("calendar.appointment.noNotesText")
            );
        },
    },

    methods: {
        /**
         * Open mobile event popover element
         * @return {void}
         */
        open() {
            this.isOpen = true;
            disableBodyScroll(document.querySelector("#mobile_event_popover"));
            if (!this.appointment.client) {
                this.appointment.client = this.appointment.user;
            }
        },

        /**
         * Close mobile event popover element
         * @return {void}
         */
        close() {
            this.isOpen = false;
            enableBodyScroll(document.querySelector("#mobile_event_popover"));
        },

        /**
         * Emit cancel appointment event
         * @emits cancelAppointment
         */
        cancelAppointment() {
            this.$emit("cancelAppointment", this.appointment);
        },

        /**
         * Emit edit appointment event
         * @emits editAppointment
         */
        editAppointment() {
            this.$emit("editAppointment", this.appointment);
        },

        /**
         * Emit approve appointment event
         * @emits approveAppointment
         */
        approveAppointment() {
            this.$emit("approveAppointment", this.appointment);
        },

        /**
         * Check if appointment can be approved
         * @param {Object} appointment
         * @return {Boolean}
         */
        canApproveAppointment(appointment) {
            return appointment?.status === "PENDING";
        },

        /**
         * Emit editBlockTime event
         * @emits editBlockTime
         */
        editBlockTime() {
            this.$emit("editBlockTime", this.appointment);
        },

        /**
         * Emit removeBlockTime event
         * @emits removeBlockTime
         */
        removeBlockTime() {
            this.$emit("removeBlockTime", this.appointment);
        },

        /**
         * Check if current user can manage appointment
         * @return {Boolean}
         */
        canManageAppointment() {
            if (
                this.appointment.employee.id ===
                this.$store.getters.currentUser.id
            ) {
                return true;
            }
            if (
                this.$hasRole(this.acl.roles.staffMember) &&
                this.$hasOnlyBasePermission()
            ) {
                return false;
            }
            return true;
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/sass/bb-styles/components/mobile-event-popover.scss";
</style>
